import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, WritableSignal, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AdminService, Plan, PlanConfig } from '../../../core/admin/admin';

interface PlanRow {
  plan: Plan;
  form: FormGroup<{
    maxContestants: FormControl<number>;
    maxActiveCompetitions: FormControl<number>;
    maxAchievements: FormControl<number>;
  }>;
  saving: WritableSignal<boolean>;
  saveError: WritableSignal<string | null>;
  saved: WritableSignal<boolean>;
}

/** F-021 Milestone A — one row per Plan, each with its own form/save state,
 *  so saving one plan's limits can't be blocked or clobbered by another's. */
@Component({
  selector: 'app-plan-config',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './plan-config.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './plan-config.css',
})
export class PlanConfigPage implements OnInit {
  private readonly admin = inject(AdminService);

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly rows = signal<PlanRow[]>([]);

  async ngOnInit(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const configs = await this.admin.listPlanConfig();
      this.rows.set(configs.map((c) => this.buildRow(c)));
    } catch {
      this.errorMessage.set('Could not load plan limits — try again in a moment.');
    } finally {
      this.loading.set(false);
    }
  }

  async save(row: PlanRow): Promise<void> {
    row.saving.set(true);
    row.saveError.set(null);
    row.saved.set(false);
    try {
      await this.admin.updatePlanConfig(row.plan, row.form.getRawValue());
      row.saved.set(true);
      setTimeout(() => row.saved.set(false), 2000);
    } catch (err) {
      row.saveError.set(this.errorMessageFor(err));
    } finally {
      row.saving.set(false);
    }
  }

  planLabel(plan: Plan): string {
    const labels: Record<Plan, string> = {
      FREE: 'Free',
      TIER_1: 'Tier 1',
      SITE_ADMIN: 'Site Admin (no limits)',
    };
    return labels[plan];
  }

  private buildRow(config: PlanConfig): PlanRow {
    return {
      plan: config.plan,
      form: new FormGroup({
        maxContestants: new FormControl(config.maxContestants, {
          nonNullable: true,
          validators: [Validators.required, Validators.min(1)],
        }),
        maxActiveCompetitions: new FormControl(config.maxActiveCompetitions, {
          nonNullable: true,
          validators: [Validators.required, Validators.min(1)],
        }),
        maxAchievements: new FormControl(config.maxAchievements, {
          nonNullable: true,
          validators: [Validators.required, Validators.min(1)],
        }),
      }),
      saving: signal(false),
      saveError: signal<string | null>(null),
      saved: signal(false),
    };
  }

  private errorMessageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const message = (err.error as { message?: string } | null)?.message;
      if (message) return message;
    }
    return 'Something went wrong — try again in a moment.';
  }
}
